import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
} from '@mui/material';
import {
  TimeToLeave,
  AccountBalanceWallet,
  Person,
  CheckCircle,
  Cancel,
  FlightTakeoff,
  Work,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

const EmployeeDashboard: React.FC = () => {
  const { user } = useAuth();
  const isApprover = user?.employee?.access === 'approver';

  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<any[]>([]);
  const [pendingLeaves, setPendingLeaves] = useState<any[]>([]);
  const [pendingTrips, setPendingTrips] = useState<any[]>([]);
  const [pendingRequisitions, setPendingRequisitions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Approve/reject dialog
  const [dialog, setDialog] = useState<{ open: boolean; item: any; type: 'leave' | 'trip' | 'requisition'; action: 'approve' | 'reject' }>({ open: false, item: null, type: 'leave', action: 'approve' });
  const [note, setNote] = useState('');

  useEffect(() => { fetchEmployeeData(); }, []);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      const [requests, balances, pendingReqs] = await Promise.all([
        apiService.getLeaveRequests().catch(() => []),
        apiService.getMyLeaveBalances().catch(() => []),
        apiService.getPendingRequisitionApprovals().catch(() => []),
      ]);
      setLeaveRequests(requests.slice(0, 5));
      setLeaveBalances(balances);
      setPendingRequisitions(pendingReqs);

      if (isApprover) {
        const [pendLeave, allTrips] = await Promise.all([
          apiService.getPendingLeaveForApprover().catch(() => []),
          apiService.getBusinessTrips().catch(() => []),
        ]);
        setPendingLeaves(pendLeave);
        setPendingTrips(allTrips.filter((t: any) =>
          t.status === 'pending_manager' &&
          t.employee?.managerId === user?.employee?.id
        ));
      }
    } catch (err: any) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveLeave = async () => {
    try {
      await apiService.approveLeaveRequest(dialog.item.id, {
        status: dialog.action === 'approve' ? 'approved' : 'rejected',
        notes: note,
      });
      setDialog({ open: false, item: null, type: 'leave', action: 'approve' });
      const pending = await apiService.getPendingLeaveForApprover();
      setPendingLeaves(pending);
    } catch (e) { console.error(e); }
  };

  const handleApproveTrip = async () => {
    try {
      await apiService.approveBusinessTrip(dialog.item.id, {
        action: dialog.action,
        rejectionReason: note || undefined,
      });
      setDialog({ open: false, item: null, type: 'trip', action: 'approve' });
      const allTrips = await apiService.getBusinessTrips();
      setPendingTrips(allTrips.filter((t: any) =>
        ['pending_manager', 'pending_cfo'].includes(t.status) &&
        t.employee?.managerId === user?.employee?.id
      ));
    } catch (e) { console.error(e); }
  };

  const handleApproveRequisition = async () => {
    try {
      if (dialog.action === 'approve') {
        await apiService.approveRequisition(dialog.item.requisitionId || dialog.item.requisition?.id, note || undefined);
      } else {
        await apiService.rejectRequisition(dialog.item.requisitionId || dialog.item.requisition?.id, note || undefined);
      }
      setDialog({ open: false, item: null, type: 'requisition', action: 'approve' });
      const updated = await apiService.getPendingRequisitionApprovals().catch(() => []);
      setPendingRequisitions(updated);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || 'Failed to process approval');
    }
  };

  const openDialog = (item: any, type: 'leave' | 'trip' | 'requisition', action: 'approve' | 'reject') => {
    setNote('');
    setDialog({ open: true, item, type, action });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  const ANNUAL_LEAVE_ID = '21156250-96bc-4f44-831d-bf29a4c11dfc';
  const annualLeaveBalance = leaveBalances.find(b => b.leaveTypeId === ANNUAL_LEAVE_ID || b.leaveType?.id === ANNUAL_LEAVE_ID);
  const annualLeaveBalanceDays = annualLeaveBalance ? parseFloat(annualLeaveBalance.closingBalanceDays || 0) : 0;
  const totalPending = pendingLeaves.length + pendingTrips.length + pendingRequisitions.length;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {getGreeting()}, {user?.employee?.firstName || user?.employee?.nickname || 'there'}!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {isApprover ? 'Here\'s what needs your attention today.' : 'Welcome to your employee portal.'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>Leave Balance</Typography>
                  <Typography variant="h4" color="primary">{annualLeaveBalanceDays}</Typography>
                  <Typography variant="body2" color="text.secondary">annual leave days available</Typography>
                </Box>
                <TimeToLeave sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    {isApprover ? 'Pending Your Approval' : 'Pending Requests'}
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {isApprover ? totalPending : leaveRequests.filter(r => r.status === 'pending').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">awaiting action</Typography>
                </Box>
                <AccountBalanceWallet sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>Profile Status</Typography>
                  <Typography variant="h6" color="success.main">Active</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isApprover ? 'Approver' : user?.role?.replace('_', ' ').toUpperCase()}
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* ── APPROVER SECTION ── */}
        {isApprover && (
          <>
            {/* Pending Leave Approvals */}
            <Grid size={12}>
              <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <TimeToLeave color="warning" />
                  <Typography variant="h6">Pending Leave Approvals</Typography>
                  <Chip label={pendingLeaves.length} color={pendingLeaves.length > 0 ? 'warning' : 'default'} size="small" />
                </Box>
                {pendingLeaves.length === 0 ? (
                  <Typography color="text.secondary">No pending leave requests — all clear ✅</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>From</TableCell>
                          <TableCell>To</TableCell>
                          <TableCell>Days</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingLeaves.map((req) => (
                          <TableRow key={req.id} hover>
                            <TableCell>{req.employee ? `${req.employee.firstName} ${req.employee.lastName}` : '-'}</TableCell>
                            <TableCell>{req.leaveType?.name || '-'}</TableCell>
                            <TableCell>{new Date(req.startDate).toLocaleDateString()}</TableCell>
                            <TableCell>{new Date(req.endDate).toLocaleDateString()}</TableCell>
                            <TableCell>{req.totalDays}</TableCell>
                            <TableCell>{req.reason || '-'}</TableCell>
                            <TableCell align="right">
                              <Button size="small" color="success" variant="contained" sx={{ mr: 1 }}
                                onClick={() => openDialog(req, 'leave', 'approve')}>
                                Approve
                              </Button>
                              <Button size="small" color="error" variant="outlined"
                                onClick={() => openDialog(req, 'leave', 'reject')}>
                                Reject
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>

            {/* Pending Business Trip Approvals */}
            <Grid size={12}>
              <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <FlightTakeoff color="info" />
                  <Typography variant="h6">Pending Business Trip Approvals</Typography>
                  <Chip label={pendingTrips.length} color={pendingTrips.length > 0 ? 'info' : 'default'} size="small" />
                </Box>
                {pendingTrips.length === 0 ? (
                  <Typography color="text.secondary">No pending trips — all clear ✅</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>Destination</TableCell>
                          <TableCell>Departure</TableCell>
                          <TableCell>Return</TableCell>
                          <TableCell>Purpose</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingTrips.map((trip) => (
                          <TableRow key={trip.id} hover>
                            <TableCell>{trip.employee ? `${trip.employee.firstName} ${trip.employee.lastName}` : '-'}</TableCell>
                            <TableCell>{trip.destination}</TableCell>
                            <TableCell>{new Date(trip.departureDate).toLocaleDateString()}</TableCell>
                            <TableCell>{new Date(trip.returnDate).toLocaleDateString()}</TableCell>
                            <TableCell>{trip.purpose || '-'}</TableCell>
                            <TableCell align="right">
                              <Button size="small" color="success" variant="contained" sx={{ mr: 1 }}
                                onClick={() => openDialog(trip, 'trip', 'approve')}>
                                Approve
                              </Button>
                              <Button size="small" color="error" variant="outlined"
                                onClick={() => openDialog(trip, 'trip', 'reject')}>
                                Reject
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>

            <Grid size={12}><Divider /></Grid>
          </>
        )}

        {/* Pending Requisition Approvals — shown to any logged-in user who has one */}
        {pendingRequisitions.length > 0 && (
          <Grid size={12}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Work color="secondary" />
                <Typography variant="h6">Pending Requisition Approvals</Typography>
                <Chip label={pendingRequisitions.length} color="secondary" size="small" />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Position</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Hiring Manager</TableCell>
                      <TableCell>Your Role</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingRequisitions.map((approval: any) => (
                      <TableRow key={approval.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {approval.requisition?.title || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{approval.requisition?.department?.name || '—'}</TableCell>
                        <TableCell>
                          {approval.requisition?.hiringManager
                            ? `${approval.requisition.hiringManager.firstName} ${approval.requisition.hiringManager.lastName}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={approval.approverRole?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Approver'}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell>
                          {approval.createdAt ? new Date(approval.createdAt).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" color="success" variant="contained" sx={{ mr: 1 }}
                            onClick={() => openDialog(approval, 'requisition', 'approve')}>
                            Approve
                          </Button>
                          <Button size="small" color="error" variant="outlined"
                            onClick={() => openDialog(approval, 'requisition', 'reject')}>
                            Reject
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* Leave Balances */}
        {leaveBalances.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Leave Balances</Typography>
              <List>
                {leaveBalances.map((balance) => (
                  <ListItem key={balance.id} divider>
                    <ListItemText
                      primary={balance.leaveType?.name}
                      secondary={`Used: ${parseFloat(balance.usedDays || 0)} | Available: ${parseFloat(balance.closingBalanceDays || 0)}`}
                    />
                    <Chip
                      label={`${parseFloat(balance.closingBalanceDays || 0)} days`}
                      color={parseFloat(balance.closingBalanceDays) > 5 ? 'success' : 'warning'}
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {/* My Recent Leave Requests */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">My Leave Requests</Typography>
              <Button size="small" onClick={() => window.location.href = '/leave'}>View All</Button>
            </Box>
            <List>
              {leaveRequests.length > 0 ? leaveRequests.map((req) => (
                <ListItem key={req.id} divider>
                  <ListItemText
                    primary={`${req.leaveType?.name || 'Leave'} — ${req.totalDays} days`}
                    secondary={`${new Date(req.startDate).toLocaleDateString()} → ${new Date(req.endDate).toLocaleDateString()}`}
                  />
                  <Chip label={req.status.replace('_', ' ').toUpperCase()} color={getStatusColor(req.status)} size="small" />
                </ListItem>
              )) : (
                <ListItem>
                  <ListItemText primary="No leave requests" secondary="Submit your first leave request to get started" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid size={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Button variant="contained" fullWidth onClick={() => window.location.href = '/leave'} startIcon={<TimeToLeave />}>
                  Request Leave
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Button variant="outlined" fullWidth onClick={() => window.location.href = '/leave'}>
                  View Leave History
                </Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Button variant="outlined" fullWidth onClick={() => window.location.href = '/business-trips'} startIcon={<FlightTakeoff />}>
                  Business Trips
                </Button>
              </Grid>
              {isApprover && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Button variant="outlined" fullWidth onClick={() => window.location.href = '/recruitment'}>
                    Recruitment
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Approve / Reject Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ ...dialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.action === 'approve' ? '✅ Approve' : '❌ Reject'}{' '}
          {dialog.type === 'leave' ? 'Leave Request' : dialog.type === 'trip' ? 'Business Trip' : 'Requisition'}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {dialog.type === 'requisition'
              ? dialog.item?.requisition?.title || 'Requisition'
              : `${dialog.item?.employee?.firstName || ''} ${dialog.item?.employee?.lastName || ''}`}
            {dialog.type === 'leave' && ` — ${dialog.item?.leaveType?.name} (${dialog.item?.totalDays} days)`}
            {dialog.type === 'trip' && ` — ${dialog.item?.destination}`}
            {dialog.type === 'requisition' && ` — ${dialog.item?.requisition?.department?.name || ''}`}
          </Typography>
          <TextField
            fullWidth multiline rows={2}
            label={dialog.action === 'approve' ? 'Note (optional)' : 'Reason for rejection'}
            value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 2 }}
            required={dialog.action === 'reject'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ ...dialog, open: false })}>Cancel</Button>
          <Button variant="contained" color={dialog.action === 'approve' ? 'success' : 'error'}
            onClick={
              dialog.type === 'leave' ? handleApproveLeave
              : dialog.type === 'trip' ? handleApproveTrip
              : handleApproveRequisition
            }>
            {dialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeDashboard;
